import { useState } from 'react'
import { Mail } from 'lucide-react'
import toast from 'react-hot-toast'
import contactService from '../services/contactService'
import MainLayout from '../components/Layout/MainLayout'
import { ACCOUNT_PAGES_HERO_IMAGE_URL } from '../constants/accountPagesHeroImage'

const SUBJECTS = ['General Inquiry', 'Support', 'Partnership', 'Other']
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

export default function ContactUsPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    subject: 'General Inquiry',
    message: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const onChange = (e) => {
    const { name, value } = e.target
    setForm((f) => ({ ...f, [name]: value }))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const nextErrors = {}
    if (!form.name.trim()) nextErrors.name = 'Name is required'
    if (!isValidEmail(form.email)) nextErrors.email = 'Valid email is required'
    if (!SUBJECTS.includes(form.subject)) nextErrors.subject = 'Subject is required'
    if (!form.message.trim()) nextErrors.message = 'Message is required'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      toast.error('Please fix the highlighted fields')
      return
    }
    setSubmitting(true)
    try {
      const data = await contactService.submitContact(form)
      toast.success('Your message has been sent')
      setErrors({})
      setForm({ name: '', email: '', company: '', subject: 'General Inquiry', message: '' })
    } catch (err) {
      toast.error(err?.message || 'Failed to send message')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <MainLayout>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <section className="relative h-96 bg-black overflow-hidden">
        <div className='h-12'></div>
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-55"
          style={{ backgroundImage: `url("${ACCOUNT_PAGES_HERO_IMAGE_URL}")` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-black/30"></div>
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse"></div>
              <p className="text-sm text-slate-300 font-medium tracking-wider uppercase">We&apos;d love to hear from you</p>
            </div>
            <h1 className="text-4xl sm:text-5xl typography-hero-title-on-dark font-bold text-white mb-3">
              Contact Us
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mb-6 font-poppinsLight">
              Have a question? Send us a message and our team will get back to you.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <h2 className="typography-card-title text-lg">Send a Message</h2>
          </div>

          <form onSubmit={onSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
                <label className="block text-sm font-poppinsMedium text-slate-700">Name<span className="text-red-500"> *</span></label>
                <input name="name" value={form.name} onChange={onChange} placeholder="Jane Doe" className={`mt-2 w-full rounded-2xl px-4 h-12 text-base border ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-slate-200 focus:border-slate-900 focus:ring-slate-900'}`} />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>
          <div>
                <label className="block text-sm font-poppinsMedium text-slate-700">Email Address<span className="text-red-500"> *</span></label>
                <input type="email" name="email" value={form.email} onChange={onChange} placeholder="jane@agency.com" className={`mt-2 w-full rounded-2xl px-4 h-12 text-base border ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-slate-200 focus:border-slate-900 focus:ring-slate-900'}`} />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>
        </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
                <label className="block text-sm font-poppinsMedium text-slate-700">Agency / Company Name</label>
                <input name="company" value={form.company} onChange={onChange} placeholder="Axon Travel Co." className="mt-2 w-full rounded-2xl px-4 h-12 text-base border border-slate-200 focus:border-slate-900 focus:ring-slate-900" />
          </div>
          <div>
                <label className="block text-sm font-poppinsMedium text-slate-700">Subject<span className="text-red-500"> *</span></label>
                <select name="subject" value={form.subject} onChange={onChange} className={`mt-2 w-full rounded-2xl px-4 h-12 text-base border ${errors.subject ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-slate-200 focus:border-slate-900 focus:ring-slate-900'}`}>
              <option>General Inquiry</option>
              <option>Support</option>
              <option>Partnership</option>
              <option>Other</option>
            </select>
                {errors.subject && <p className="mt-1 text-xs text-red-600">{errors.subject}</p>}
          </div>
        </div>

        <div>
              <label className="block text-sm font-poppinsMedium text-slate-700">Message<span className="text-red-500"> *</span></label>
              <textarea name="message" value={form.message} onChange={onChange} rows={8} placeholder="Tell us how we can help..." className={`mt-2 w-full rounded-2xl p-4 text-base min-h-[180px] border ${errors.message ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-slate-200 focus:border-slate-900 focus:ring-slate-900'}`} />
              {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
        </div>

            <div className="pt-2">
              <button type="submit" disabled={submitting} className="inline-flex items-center px-6 py-3 rounded-xl bg-gradient-to-r from-slate-800 to-slate-900 text-white hover:from-slate-900 hover:to-black disabled:opacity-60 shadow-md">
                {submitting ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </form>
          </div>

          <aside className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h3 className="typography-card-title text-base mb-3">Support</h3>
            <p className="typography-body-sm mb-4">Our team typically responds within 1-2 business days.</p>
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-slate-700 font-poppinsMedium">Email</p>
                <p className="typography-body-sm">support@secureaxonall.com</p>
              </div>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-slate-700 font-poppinsMedium">Topics</p>
                <p className="typography-body-sm">General questions, technical support, partnerships</p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
    </MainLayout>
  )
}


